// Utility functions for handling photos and metadata extraction
import EXIF from "exif-js";
import heic2any from "heic2any";

// Helper function to convert File objects or Blob URLs to Base64
export const urlToBase64 = async (urlOrFile) => {
  if (urlOrFile instanceof File) {
    // Handle File object
    // Check if it's a HEIC file
    if (urlOrFile.name && urlOrFile.name.toLowerCase().endsWith('.heic')) {
      // For HEIC files, we still convert to base64 but need to handle metadata differently
      try {
        // If heic-convert is available, you could use it here to convert to JPEG
        // For now, we'll just convert to base64 as is
        return new Promise((resolve, reject) => {
          const reader = new FileReader();
          reader.readAsDataURL(urlOrFile);
          reader.onload = () => resolve(reader.result);
          reader.onerror = (error) => reject(error);
        });
      } catch (error) {
        console.error('Error processing HEIC file:', error);
        return null;
      }
    }
    // Handle other file types normally
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(urlOrFile);
      reader.onload = () => resolve(reader.result);
      reader.onerror = (error) => reject(error);
    });
  } else if (typeof urlOrFile === "string" && urlOrFile.startsWith("blob:")) {
    // Handle Blob URL
    try {
      const response = await fetch(urlOrFile);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const blob = await response.blob();
      return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(blob);
        reader.onload = () => resolve(reader.result);
        reader.onerror = (error) => reject(error);
      });
    } catch (error) {
      console.error("Error fetching or converting blob URL:", error);
      return null; // Indicate failure
    }
  } else if (
    typeof urlOrFile === "string" &&
    urlOrFile.startsWith("data:image")
  ) {
    // Handle existing Base64 data URL
    return Promise.resolve(urlOrFile);
  } else {
    // Handle other cases (e.g., regular server URLs, null, undefined)
    console.warn(
      "Source is not a File, Blob URL, or Data URL. Cannot convert to Base64:",
      urlOrFile
    );
    return Promise.resolve(null); // Resolve with null if conversion isn't applicable/possible
  }
};



export const getPhotoLocation = (file , isGeolocationAvailable , isGeolocationEnabled , coords , positionError) => {
  return new Promise((resolve) => {
    // Function to extract location data from photos
    const getBrowserLocation = () => {
      return new Promise(async (geoResolve) => {

        if (isGeolocationAvailable && isGeolocationEnabled) {

          // Usage
          if (coords) {

            geoResolve({
              latitude: coords.latitude,
              longitude: coords.longitude
            });
          } else if (positionError) {

            geoResolve({ latitude: null, longitude: null });
          }
        } else {
          geoResolve({ latitude: null, longitude: null }); // Geolocation not supported
        }
      });
    };
    // Check if it's a HEIC file
    if (file.name && file.name.toLowerCase().endsWith('.heic')) {
      // For HEIC files, use ExifReader
      const reader = new FileReader();
      reader.onload = async function (e) {
        try {
          // Import ExifReader dynamically to avoid issues
          const ExifReader = await import('exifreader');
          const tags = ExifReader.default.load(e.target.result);

          // Check for GPS data
          if (tags.GPSLatitude && tags.GPSLongitude) {
            const latRef = tags.GPSLatitudeRef ? tags.GPSLatitudeRef.value[0] : 'N';
            const lonRef = tags.GPSLongitudeRef ? tags.GPSLongitudeRef.value[0] : 'E';

            // Extract latitude and longitude values
            let latitude = tags.GPSLatitude.description;
            let longitude = tags.GPSLongitude.description;

            // If description is in DMS format, convert to decimal
            if (typeof latitude === 'string' && latitude.includes('°')) {
              // Parse DMS format (e.g. "38° 53' 23.64\" N")
              const latParts = latitude.match(/(\d+)°\s*(\d+)'\s*(\d+\.?\d*)/);
              if (latParts) {
                latitude = parseFloat(latParts[1]) + parseFloat(latParts[2]) / 60 + parseFloat(latParts[3]) / 3600;
                if (latRef === 'S') latitude = -latitude;
              }

              const lonParts = longitude.match(/(\d+)°\s*(\d+)'\s*(\d+\.?\d*)/);
              if (lonParts) {
                longitude = parseFloat(lonParts[1]) + parseFloat(lonParts[2]) / 60 + parseFloat(lonParts[3]) / 3600;
                if (lonRef === 'W') longitude = -longitude;
              }
            } else {
              // Handle numeric values
              latitude = parseFloat(latitude);
              longitude = parseFloat(longitude);

              // Apply direction reference if needed
              if (latRef === 'S') latitude = -Math.abs(latitude);
              if (lonRef === 'W') longitude = -Math.abs(longitude);
            }


            // Store the extracted coordinates
            file.exifData = {
              latitude: latitude,
              longitude: longitude
            };

            resolve({
              latitude: latitude,
              longitude: longitude
            });
          } else {
            getBrowserLocation().then(resolve);
          }
        } catch (error) {
          console.error('Error extracting HEIC metadata:', error);
          getBrowserLocation().then(resolve);
        }
      };
      reader.onerror = function () {
        console.error('Error reading HEIC file');
        getBrowserLocation().then(resolve);
      };
      reader.readAsArrayBuffer(file);
      return;
    }

    // For regular image files, use EXIF.js
    EXIF.getData(file, function () {
      let lat = null;
      let lon = null;

      // Try to get GPS data
      const latRef = EXIF.getTag(this, "GPSLatitudeRef");
      const latArray = EXIF.getTag(this, "GPSLatitude");
      const lonRef = EXIF.getTag(this, "GPSLongitudeRef");
      const lonArray = EXIF.getTag(this, "GPSLongitude");

      // Convert coordinates if available
      if (latRef && latArray && lonRef && lonArray) {
        // Convert degrees, minutes, seconds to decimal
        lat = latArray[0] + latArray[1] / 60 + latArray[2] / 3600;
        lon = lonArray[0] + lonArray[1] / 60 + lonArray[2] / 3600;

        // Apply direction reference
        if (latRef === 'S') lat = -lat;
        if (lonRef === 'W') lon = -lon;

        console.log('Extracted GPS coordinates:', lat, lon);
        resolve({
          latitude: lat,
          longitude: lon
        });
      } else {
        console.log('No GPS data found in image, attempting to get browser location.');
        getBrowserLocation().then(resolve);
      }
    });
  });
};

export const convertHeic = async (file) => {
  const blob = await heic2any({ blob: file, toType: "image/jpeg" });
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

// Mapping of point type display names to their respective keys from API/config
export const pointTypeKeys = {
  "Nouveau point de vision": "vision_point",
  "Point Relais": "relay_point",
  "Point Haut": "high_point",
  "Centre de surveillance": "surveillance_center",
};